import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Info, Loader2, Mail, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DEFAULT_CODE_EXPIRY_MINUTES,
  SLOW_MAIL_HINT_DELAY_MS,
  formatExpiryMinutes,
  isKnownSlowMailbox,
} from "@/lib/verificationCode";

interface EmailVerificationProps {
  email: string;
  onVerify: (code: string) => Promise<void>;
  onResendCode: () => Promise<void>;
  /**
   * Leave the verification screen to correct or replace the address. When
   * omitted, the "change email" actions are not rendered.
   */
  onChangeEmail?: () => void;
  /** Minutes a code stays valid, as reported by the backend; defaults to 60. */
  expiresInMinutes?: number | null;
  isLoading?: boolean;
  error?: string;
  className?: string;
}

const RESEND_COOLDOWN_SECONDS = 60;

export const EmailVerification: React.FC<EmailVerificationProps> = ({
  email,
  onVerify,
  onResendCode,
  onChangeEmail,
  expiresInMinutes,
  isLoading = false,
  error,
  className,
}) => {
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [hasResent, setHasResent] = useState(false);
  // Hotmail/Outlook/Live/MSN addresses are the known slow case (backend#832),
  // so they get the slow-mailbox hint straight away; everyone else only once
  // the code is overdue.
  const isSlowMailbox = isKnownSlowMailbox(email);
  const [hintDelayElapsed, setHintDelayElapsed] = useState(false);
  const showSlowMailHint = isSlowMailbox || hintDelayElapsed;
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  // Single in-flight guard shared by all submit paths (auto-submit on the 6th
  // digit, paste, and the manual button). A ref flips synchronously, so a
  // manual click landing right after the auto-submit is a no-op — this is what
  // stopped the double `verify-email` POST that burned the 3/min rate limit
  // (#698). Released when the request settles so a wrong code can be retried.
  const submittingRef = useRef(false);

  const expiryMinutes = expiresInMinutes ?? DEFAULT_CODE_EXPIRY_MINUTES;

  const submitCode = async (fullCode: string) => {
    if (submittingRef.current || isLoading || fullCode.length !== 6) {
      return;
    }
    submittingRef.current = true;
    try {
      await onVerify(fullCode);
    } finally {
      submittingRef.current = false;
    }
  };

  // Handle cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Reveal the slow-mailbox hint once the code is overdue. The screen unmounts
  // on a successful verify, so "still mounted after the delay" means "not
  // verified yet".
  useEffect(() => {
    if (isSlowMailbox) {
      return;
    }
    const timer = setTimeout(() => setHintDelayElapsed(true), SLOW_MAIL_HINT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isSlowMailbox]);

  const handleCodeChange = (index: number, value: string) => {
    // Only allow numeric input
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (newCode.every(digit => digit !== "") && value) {
      submitCode(newCode.join(""));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      // Focus previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);

    if (pastedText.length === 6) {
      const newCode = pastedText.split("");
      setCode(newCode);
      inputRefs.current[5]?.focus();
      // Auto-submit pasted code
      submitCode(pastedText);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) {
      return;
    }

    setIsResending(true);
    try {
      await onResendCode();
    } catch {
      // The parent shows the error; a failed resend must not clear the code,
      // start the cooldown or claim a new code was sent.
      return;
    } finally {
      setIsResending(false);
    }
    setCode(["", "", "", "", "", ""]); // Clear current code
    setHasResent(true);
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    inputRefs.current[0]?.focus();
  };

  const handleManualSubmit = () => {
    submitCode(code.join(""));
  };

  const isCodeComplete = code.every(digit => digit !== "");
  const canResend = !isResending && resendCooldown === 0;

  return (
    <div className={cn("w-full max-w-md mx-auto", className)}>
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Mail className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-lg font-semibold mb-2">Verify Your Email</h3>
        <p className="text-sm text-muted-foreground">
          We've sent a 6-digit verification code to{" "}
          <span className="font-medium text-foreground break-all">{email}</span>
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          The code is valid for {formatExpiryMinutes(expiryMinutes)}.
        </p>
        {onChangeEmail && (
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={onChangeEmail}
            // Not while a code is being checked: a late success would still
            // advance the wizard with the old session.
            disabled={isLoading}
            className="h-auto px-0 py-1.5 mt-0.5 -mb-1.5 text-sm"
          >
            Wrong address? Change email
          </Button>
        )}
      </div>

      {/* Verification Code Input */}
      <div className="mb-6">
        <Label className="text-sm font-medium mb-3 block text-center">
          Enter verification code
        </Label>
        <div className="flex gap-2 justify-center mb-4" onPaste={handlePaste}>
          {code.map((digit, index) => (
            <Input
              key={index}
              ref={el => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              pattern="\d*"
              maxLength={1}
              value={digit}
              aria-label={`Digit ${index + 1}`}
              onChange={e => handleCodeChange(index, e.target.value)}
              onKeyDown={e => handleKeyDown(index, e)}
              className={cn(
                // Six boxes must fit a 360px phone inside the card's padding.
                "w-10 h-11 px-0 sm:w-12 sm:h-12 text-center text-lg font-semibold",
                "focus:ring-2 focus:ring-primary/20",
                digit && "border-primary"
              )}
              disabled={isLoading}
            />
          ))}
        </div>

        {/* Manual Submit Button (shows when code is complete but not auto-submitted) */}
        {isCodeComplete && !isLoading && (
          <div className="text-center mb-4">
            <Button onClick={handleManualSubmit} className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Verifying...
                </>
              ) : (
                "Verify Code"
              )}
            </Button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="text-center mb-4">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Verifying your code...
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Slow-mailbox hint (backend#832) */}
      {showSlowMailHint ? (
        <Alert className="mb-6 text-left bg-muted/40" data-testid="slow-mail-hint">
          <Info className="h-4 w-4" />
          <AlertTitle>Can't find the email?</AlertTitle>
          <AlertDescription className="text-muted-foreground">
            <p>
              It can take a while to reach some inboxes, especially Hotmail and Outlook. Check your
              Junk folder and, in Outlook, the Other tab.
            </p>
            {onChangeEmail && (
              <p className="mt-2">
                Still nothing?{" "}
                <Button
                  type="button"
                  variant="link"
                  onClick={onChangeEmail}
                  disabled={isLoading}
                  className="h-auto px-0 py-1 -my-1 text-sm align-baseline whitespace-normal text-left"
                >
                  Use a different email address
                </Button>
              </p>
            )}
          </AlertDescription>
        </Alert>
      ) : (
        <p className="mb-6 text-center text-xs text-muted-foreground">
          The email usually arrives within a minute. Check your Junk or Spam folder if you don't see
          it.
        </p>
      )}

      {/* Resend Code */}
      <div className="text-center">
        <p className="text-sm text-muted-foreground mb-2">Didn't receive the code?</p>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleResendCode}
          disabled={!canResend}
          className="h-auto px-0 py-1.5 -my-1.5 font-medium text-primary hover:text-primary/80"
        >
          {isResending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Sending...
            </>
          ) : resendCooldown > 0 ? (
            `Resend code in ${resendCooldown}s`
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-1" />
              Resend verification code
            </>
          )}
        </Button>
        {/* Resending no longer cancels earlier codes (backend#832), so an
            email that turns up late still works — say so, or applicants
            ignore the first email once they have asked for another. */}
        <p className="mt-2 text-xs text-muted-foreground" aria-live="polite">
          {hasResent
            ? "New code sent. The codes from your last three emails all work, so enter whichever arrives first."
            : "Sending a new code doesn't cancel the one we already sent."}
        </p>
      </div>
    </div>
  );
};
